/**
 * US Census 4-region grouping for the consolidated geography input
 * (`GeographySelector`). Front-end only — this is a UI convenience for bulk
 * applying a preference to a block of states; the stored answer is still a flat
 * `stateCode → yes|maybe|no` map, so regions never touch the data model.
 *
 * The four Census regions cover the 50 states + DC (DC sits in the South, per
 * the Census Bureau). The US territories carried by `US_STATE_OPTIONS` (PR, GU,
 * VI) belong to no Census region, so they get their own `Territories` group at
 * the end.
 *
 * Region membership is by state *code* only — state display labels come from
 * the question config at runtime (passed into `GeographySelector` as props), so
 * this module stays free of any server-config import.
 */
export interface GeoRegion {
  /** Stable key, also the display label. */
  name: string;
  /** State codes (matching `US_STATE_OPTIONS` values) in this region. */
  states: string[];
}

/**
 * The four Census regions plus a Territories catch-all. Order is the order the
 * region rows render in. Each state code appears in exactly one group (see
 * `regions.test.ts`).
 */
export const GEO_REGIONS: GeoRegion[] = [
  {
    name: 'Northeast',
    states: ['CT', 'ME', 'MA', 'NH', 'NJ', 'NY', 'PA', 'RI', 'VT'],
  },
  {
    name: 'Midwest',
    states: ['IL', 'IN', 'IA', 'KS', 'MI', 'MN', 'MO', 'NE', 'ND', 'OH', 'SD', 'WI'],
  },
  {
    name: 'South',
    states: [
      'AL', 'AR', 'DC', 'DE', 'FL', 'GA', 'KY', 'LA', 'MD', 'MS',
      'NC', 'OK', 'SC', 'TN', 'TX', 'VA', 'WV',
    ],
  },
  {
    name: 'West',
    states: ['AK', 'AZ', 'CA', 'CO', 'HI', 'ID', 'MT', 'NV', 'NM', 'OR', 'UT', 'WA', 'WY'],
  },
  {
    name: 'Territories',
    states: ['PR', 'GU', 'VI'],
  },
];

/** State code → its region name, for grouping chips and region highlighting. */
export const REGION_BY_STATE: Record<string, string> = Object.fromEntries(
  GEO_REGIONS.flatMap((r) => r.states.map((code) => [code, r.name]))
);

/**
 * The jurisdictions with active community-solar markets. When the geography
 * editor is scoped to the Community Solar segment, these are its default
 * markets — the bulk "All community solar markets" control and region rows act
 * on them. Buyers can still add any other state via the type-ahead (front-end
 * only — the stored answer map is unaffected). Codes match `US_STATE_OPTIONS`;
 * "DC" is the District of Columbia.
 */
export const COMMUNITY_SOLAR_STATES: string[] = [
  'CO', 'DC', 'IL', 'MA', 'MD', 'ME', 'MN', 'NH', 'NJ', 'NM', 'NY', 'OR', 'VA', 'VT',
];
